import { ChangeEvent, useEffect, useState } from 'react';
import { Box, Skeleton, Stack } from '@mui/material';
import _ from 'lodash';
import CardFrom, { CardFormChildrenConfig, CardFormConfig } from '../../component/card/card-from';
import i18n from '../../i18n';
import ButtonComponent from '../../component/button/button.component';
import GlobalNotification from '../../component/common/snackbar';
import apiManage from '../../request';
import { Restaurant } from '../../models';
import { mapRestaurantOriginErrorToI18nKey } from '../../utils/map-restaurant-origin-error-to-i18n-key';

/** 营业分析数据来源下拉选项：EFR / ECCO，供 CardFrom 表单项使用 */
const CLOUD_ANALYSIS_ORIGIN_OPTIONS: { value: string; label: string }[] = [
    { value: 'ECCO', label: 'Ecco/CWA' },
    { value: 'EFR', label: 'EFR/CWA FR' },
];

/** 餐厅设置页表单编辑数据，仅包含需提交的字段 */
type EditData = Pick<Restaurant, 'cloud_analysis_origin'>;

/**
 * 餐厅设置页：与当前餐厅相关的配置（如营业分析数据来源 EFR/ECCO），与「营业分析」平级入口。
 * 加载当前餐厅信息并调用 PATCH cloud-analysis-origin 保存。
 */
export default function RestaurantSettingsPage() {
    /** 是否处于加载中（拉取餐厅信息），用于展示 Skeleton */
    const [loading, setLoading] = useState<boolean>(true);
    /** 当前餐厅信息（来自 GET 云端餐厅信息），用于对比是否已编辑 */
    const [restaurant, setRestaurant] = useState<Restaurant>({ cloud_analysis_origin: 'ECCO' });
    /** 表单当前编辑值，默认 ECCO；提交时取此值调用 PATCH */
    const [editData, setEditData] = useState<EditData>({ cloud_analysis_origin: 'ECCO' });

    useEffect(() => {
        loadRestaurant();
    }, []);

    /**
     * 拉取当前餐厅云端信息并回填表单；接口未返回 cloud_analysis_origin 时按 ECCO 回显。
     * 接口返回 error 或异常时提示用户并保持当前表单状态。
     */
    const loadRestaurant = async () => {
        setLoading(true);
        try {
            /** GET 云端餐厅信息接口返回，含 cloud_analysis_origin 或 error */
            const res = await apiManage.get_cloud_restaurant_info();
            if (res && !_.get(res, 'error')) {
                /** 营业分析数据来源，空则用默认 ECCO */
                const origin = res.cloud_analysis_origin ?? 'ECCO';
                setRestaurant({
                    ...res,
                    cloud_analysis_origin: origin,
                });
                setEditData({
                    cloud_analysis_origin: origin,
                });
            } else if (res?.error) {
                GlobalNotification.error(i18n.t('restaurant_page_request_text_failed_to_get_restaurant_information'));
            }
        } finally {
            /** 无论成功、失败或抛错，保证 loading 状态被重置，避免界面卡在 Skeleton */
            setLoading(false);
        }
    };

    /**
     * 表单字段变更回调，更新 editData 中对应 key 的值
     * @param value 当前字段值（字符串或布尔）
     * @param key 字段 key，如 cloud_analysis_origin
     */
    const onFormChange = (value: string | boolean, key: string) => {
        setEditData((prev) => ({ ...prev, [key]: value }));
    };

    /**
     * 保存表单：校验必选 EFR/ECCO 后调用 PATCH cloud-analysis-origin，成功则提示并重新拉取
     */
    const onFormSave = async () => {
        /** 当前选择的营业分析数据来源，需为 EFR 或 ECCO 才允许提交 */
        const origin = editData.cloud_analysis_origin;
        if (origin !== 'EFR' && origin !== 'ECCO') {
            GlobalNotification.error(i18n.t('settings_page_text_cloud_analysis_origin_please_select'));
            return;
        }
        /** PATCH 云端营业分析数据来源接口的返回，含 error 表示失败 */
        const result = await apiManage.patch_cloud_analysis_origin(origin);
        if (result && !_.get(result, 'error')) {
            GlobalNotification.success(i18n.t('dish_book_msg_save_success'));
            setEditData({
                cloud_analysis_origin: origin,
            });
            /** 使用函数式更新避免闭包中的 restaurant 陈旧，覆盖并发下的最新 state */
            setRestaurant((prev) => ({ ...prev, cloud_analysis_origin: origin }));
        } else {
            /** 根据后端校验详情显示双隧道专用提示，未知错误仍使用通用提示。 */
            const errorKey = mapRestaurantOriginErrorToI18nKey(result);
            GlobalNotification.error(i18n.t(errorKey));
        }
    };

    /** CardFrom 卡片内表单项配置：营业分析数据来源下拉 */
    const formChildren: CardFormChildrenConfig[] = [
        {
            key: 'cloud_analysis_origin',
            label: i18n.t('settings_page_text_cloud_analysis_origin'),
            xs: 12,
            md: 12,
            comType: 'INPUT',
            inputType: 'SELECT',
            items: CLOUD_ANALYSIS_ORIGIN_OPTIONS,
            inputIsEdited: restaurant?.cloud_analysis_origin !== editData.cloud_analysis_origin,
            onChange: (e: ChangeEvent<HTMLInputElement | { value: unknown }>) => onFormChange(_.get(e, 'target.value', e) as string ?? '', 'cloud_analysis_origin'),
        },
    ];

    /** 整页卡片配置：标题、保存按钮、子表单项 */
    const cardConfig: CardFormConfig[] = [
        {
            key: 'restaurant_settings',
            md: 12,
            columnSpacing: { xs: 2 },
            rowSpacing: 4,
            title: i18n.t('base_setting_title'),
            actionRender: (
                <ButtonComponent action="SAVE" handleClick={() => onFormSave()} async={true} />
            ),
            children: formChildren,
        },
    ];

    /** 加载中时展示与卡片布局一致的 Skeleton（标题栏 + 表单项区域） */
    if (loading) {
        return (
            <div className="restaurant-settings">
                <Box sx={{ maxWidth: 600 }}>
                    <Stack spacing={0}>
                        <Skeleton variant="rounded" height={56} animation="wave" sx={{ mb: 2 }} />
                        <Skeleton variant="rounded" height={80} animation="wave" />
                    </Stack>
                </Box>
            </div>
        );
    }

    return (
        <div className="restaurant-settings">
            <Box sx={{ maxWidth: 600 }}>
                <CardFrom config={cardConfig} data={editData} />
            </Box>
        </div>
    );
}
